
import React, { useState } from 'react';
import { ItemDraft, ManagerDetails, Campaign, Aggregator } from '../types';
import { Check, Layers, AlertCircle, User, Image as ImageIcon, Tag, ArrowLeft } from 'lucide-react';
import { asArray } from '../utils/asArray';

interface IdentityConfirmProps {
  brandNames: string[];
  items: ItemDraft[];
  managerDetails: ManagerDetails;
  setManagerDetails: React.Dispatch<React.SetStateAction<ManagerDetails>>;
  generalAggregators: string[];
  setGeneralAggregators: React.Dispatch<React.SetStateAction<string[]>>;
  onBack: () => void;
  onSubmit: () => void;
  campaign?: Campaign | null;
  aggregators?: Aggregator[];
  currencyCode?: string;
}

export const IdentityConfirm: React.FC<IdentityConfirmProps> = ({
  brandNames,
  items,
  managerDetails,
  setManagerDetails,
  generalAggregators,
  setGeneralAggregators,
  onBack,
  onSubmit,
  campaign,
  aggregators = [],
  currencyCode,
}) => {
  const currency = currencyCode || 'QAR';
  const [errors, setErrors] = useState<Record<string, string>>({});
  const safeItems = asArray(items);
  const safeCampaignAggregators = asArray(campaign?.aggregators || []);
  const safeAggregators = asArray(aggregators);
  const safeGeneralAggregators = asArray(generalAggregators);

  const handleConfirm = () => {
    // If no campaign is present (fallback), ensure manual selection
    if (!campaign && safeGeneralAggregators.length === 0) {
      setErrors({ generalAggregators: 'Select at least one' });
      return;
    }
    onSubmit();
  };

  const toggleGeneralAggregator = (aggregator: string) => {
    // Only allow toggling if no campaign is active (fallback mode)
    if (campaign) return; 
    
    let newAggregators: string[];
    if (safeGeneralAggregators.includes(aggregator)) {
      newAggregators = safeGeneralAggregators.filter(a => a !== aggregator);
    } else {
      newAggregators = [...safeGeneralAggregators, aggregator];
    }
    setGeneralAggregators(newAggregators);
  };

  return (
    <div className="max-w-3xl mx-auto pb-32">
      <h2 className="text-3xl font-bold text-zinc-900 tracking-tight mb-8">Finalize Submission</h2>

      <div className="space-y-6">
        
        {/* Manager Read-Only Summary */}
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-zinc-100 flex items-center gap-5">
           <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center text-teal-600 border border-teal-100">
              <User className="w-8 h-8" />
           </div>
           <div>
              <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1">Submitting As</h3>
              <div className="text-xl font-bold text-zinc-900">{managerDetails.name}</div>
              <div className="text-sm text-zinc-500 font-mono">ID: {managerDetails.fileNumber}</div>
           </div>
        </div>

        {/* Campaign Info / Aggregators */}
        <div className="bg-white rounded-3xl p-8 shadow-sm border border-zinc-100">
            {campaign ? (
                // Campaign Mode: Read Only Display
                <div className="flex flex-col md:flex-row gap-8">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-4">
                            <Layers className="w-5 h-5 text-zinc-400" />
                            <h3 className="text-lg font-bold text-zinc-800">Participating Platforms</h3>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {safeCampaignAggregators.map(agg => (
                                <span key={agg} className="px-4 py-2 bg-zinc-50 text-zinc-700 border border-zinc-200 rounded-xl text-sm font-bold flex items-center gap-2">
                                    <Check className="w-3.5 h-3.5 text-teal-500" /> {agg}
                                </span>
                            ))}
                        </div>
                        <p className="text-xs text-zinc-400 mt-3 font-medium">These platforms will be applied to your submission automatically.</p>
                    </div>

                    <div className="w-px bg-zinc-100 hidden md:block"></div>

                    <div className="md:w-1/3 space-y-4">
                         <div className="bg-teal-50 rounded-2xl p-4 border border-teal-100 text-center">
                            <div className="flex items-center justify-center gap-2 mb-1">
                              <Tag className="w-5 h-5 text-teal-600" />
                              <h3 className="text-sm font-bold text-teal-800 uppercase tracking-wide">Campaign Discount</h3>
                            </div>
                            <div className="text-3xl font-extrabold text-teal-700">
                                {campaign.discountValue}
                              <span className="text-lg ml-1">{campaign.discountType === 'Percentage' ? '%' : ` ${currency}`}</span>
                            </div>
                            <div className="text-[11px] font-bold text-teal-600 uppercase tracking-wider mt-1">
                                {campaign.discountType} OFF
                            </div>
                        </div>
                        <div className="bg-white rounded-2xl p-4 border border-teal-100 text-center">
                            <div className="flex items-center justify-center gap-2 mb-1">
                              <Tag className="w-5 h-5 text-zinc-400" />
                              <h3 className="text-sm font-bold text-zinc-800 uppercase tracking-wide">Campaign Funding</h3>
                            </div>
                            <div className="text-xl font-extrabold text-teal-700">
                                {campaign.fundingType || 'None'}
                            </div>
                            <div className="text-sm font-bold text-teal-600 mt-1">
                                {campaign.fundingType === 'Co-Funded' && (
                                  campaign.discountType === 'Percentage' 
                                    ? <>Sterling {campaign.sterlingPercent ?? '-'}% / Partner {campaign.coFundedPercent ?? '-'}%</>
                                    : (() => {
                                        const dv = Number(campaign.discountValue) || 0;
                                        const s = campaign.sterlingPercent ?? 0;
                                        const p = campaign.coFundedPercent ?? 0;
                                        return <>Sterling {currency} {(dv * (s/100)).toFixed(2)} / Partner {currency} {(dv * (p/100)).toFixed(2)}</>;
                                      })()
                                )}
                                {campaign.fundingType === 'Fully Funded' && (
                                  campaign.discountType === 'Percentage'
                                    ? <>Sterling {campaign.sterlingPercent ?? 100}%</>
                                    : <>Sterling {currency} {(Number(campaign.discountValue) || 0).toFixed(2)}</>
                                )}
                                {(!campaign.fundingType || campaign.fundingType === 'None') && 'No funding breakdown'}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                // Fallback Mode: Manual Selection
                <>
                    <div className="flex justify-between items-start mb-6">
                        <div>
                            <h3 className="text-lg font-bold text-zinc-800">Choose Aggregators</h3>
                            <p className="text-sm text-zinc-500 mt-1">Select applicable platforms.</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {safeAggregators.filter(agg => agg.active).map(agg => {
                            const isSelected = safeGeneralAggregators.includes(agg.name);
                            return (
                            <button
                                key={agg.id}
                                onClick={() => toggleGeneralAggregator(agg.name)}
                                className={`
                                    px-6 py-3 rounded-2xl text-sm font-bold transition-all duration-200
                                    ${isSelected 
                                        ? 'bg-zinc-900 text-white shadow-lg shadow-zinc-900/20 scale-105' 
                                        : 'bg-zinc-50 text-zinc-600 hover:bg-zinc-100'
                                    }
                                `}
                            >
                              {agg.name}
                            </button>
                            );
                        })}
                    </div>
                    {errors['generalAggregators'] && (
                        <div className="mt-4 flex items-center gap-2 text-red-500 text-sm font-bold bg-red-50 p-3 rounded-xl inline-flex">
                            <AlertCircle className="w-4 h-4"/> Please select at least one
                        </div>
                    )}
                </>
            )}
        </div>

        {/* Summary */}
        <div className="bg-zinc-50 rounded-3xl p-8 border border-zinc-200">
             <div className="flex justify-between items-center mb-6">
                <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Submission Summary</span>
                <span className="bg-white px-3 py-1 rounded-lg text-sm font-bold text-zinc-700 shadow-sm border border-zinc-100">{safeItems.length} Items</span>
             </div>
             
             <div className="space-y-1">
                 {safeItems.map((item, idx) => (
                     <div key={item.id} className="flex justify-between items-center py-3 border-b border-zinc-200 last:border-0">
                         <div className="flex items-center gap-3">
                             <span className="text-zinc-300 font-bold text-xs w-6">{(idx + 1).toString().padStart(2, '0')}</span>
                             {(() => {
                                const raw = item.image;
                                let imgSrc: string | null = null;
                                if (raw) {
                                  if (raw.startsWith('data:')) {
                                    imgSrc = raw;
                                  } else if (raw.startsWith('/api/assets')) {
                                    imgSrc = raw;
                                  } else if (raw.includes('.r2.dev/')) {
                                    imgSrc = `/api/assets?key=${encodeURIComponent(raw.split('.r2.dev/')[1])}`;
                                  } else if (raw.includes('sterlingops-assets/')) {
                                    imgSrc = `/api/assets?key=${encodeURIComponent(raw.split('sterlingops-assets/')[1])}`;
                                  } else if (raw.startsWith('uploads/')) {
                                    imgSrc = `/api/assets?key=${encodeURIComponent(raw)}`;
                                  } else if (raw.startsWith('http')) {
                                    imgSrc = raw;
                                  } else {
                                    imgSrc = `data:image/png;base64,${raw}`;
                                  }
                                }
                                return imgSrc ? (
                                  <div className="w-10 h-10 rounded-lg border border-zinc-200 overflow-hidden bg-white shrink-0">
                                      <img src={imgSrc} alt="thumbnail" className="w-full h-full object-contain" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                                  </div>
                                ) : (
                                  <div className="w-10 h-10 rounded-lg border border-zinc-200 bg-white flex items-center justify-center text-zinc-300 shrink-0">
                                      <ImageIcon className="w-5 h-5" />
                                  </div>
                                );
                             })()}
                             <span className="font-medium text-zinc-700">{item.name || "Untitled"}</span>
                         </div>
                         <span className="font-mono text-teal-600 font-bold text-sm">{currency} {item.sellingPrice || "0.00"}</span>
                     </div>
                 ))}
             </div>
        </div>

      </div>

      <div className="fixed bottom-0 left-0 right-0 p-6 bg-white/80 backdrop-blur-lg border-t border-zinc-200/60 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
           <button 
              onClick={onBack}
              className="px-6 py-3 rounded-2xl text-zinc-600 font-bold hover:bg-zinc-100 transition-colors"
           >
              Back
           </button>
           
           <button
              onClick={handleConfirm}
              className="flex items-center px-8 py-4 bg-teal-600 text-white text-base font-bold rounded-full shadow-lg hover:bg-teal-500 hover:scale-105 active:scale-95 transition-all shadow-teal-900/20"
           >
              Submit Now
              <Check className="ml-2 h-5 w-5" />
           </button>
        </div>
      </div>
    </div>
  );
};
